import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Building2, ChevronLeft, ChevronRight, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import {
  PageHeaderActionsLeading,
  PageHeaderTitleOverride,
} from "@/contexts/PageHeaderContext";
import { formatApiErrorDetail } from "@/lib/api";
import { can, ROLE_COLORS, ROLE_LABELS } from "@/lib/rbac";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 10;

const emptyBilling = {
  gst_number: "",
  pan_number: "",
  billing_name: "",
  billing_address: "",
  billing_email: "",
  billing_phone: "",
  bank_name: "",
  bank_account_name: "",
  bank_account_number: "",
  bank_ifsc: "",
};

const BILLING_FIELDS = [
  ["billing_name", "Billing name", "Legal billing name"],
  ["gst_number", "GST number", "33AABCA1234C1Z5"],
  ["pan_number", "PAN number", "AABCA1234C"],
  ["billing_email", "Billing email", "billing@example.com"],
  ["billing_phone", "Billing phone", "+91-44-28290201"],
  ["billing_address", "Billing address", "Street, city, pincode"],
  ["bank_name", "Bank name", "HDFC Bank"],
  ["bank_account_name", "Account name", "Account holder name"],
  ["bank_account_number", "Account number", "50200012345678"],
  ["bank_ifsc", "IFSC", "HDFC0001234"],
];

const BYTES_PER_GB = 1024 ** 3;

const bytesToGbInput = (bytes) => {
  if (bytes == null || bytes === "") return "";
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return "";
  const gb = n / BYTES_PER_GB;
  return String(Math.round(gb * 100) / 100);
};

const gbInputToBytes = (gb) => {
  const trimmed = String(gb ?? "").trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return NaN;
  return Math.round(n * BYTES_PER_GB);
};

const formatGbAmount = (bytes) => {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return null;
  const gb = Math.round((n / BYTES_PER_GB) * 10) / 10;
  return Number.isInteger(gb) ? String(gb) : gb.toFixed(1);
};

const formatQuotaRatio = (usedBytes, quotaBytes) => {
  const used = formatGbAmount(usedBytes ?? 0) ?? "0";
  const total = formatGbAmount(quotaBytes);
  return total == null ? `${used}/—` : `${used}/${total}`;
};

const emptyHospitalForm = {
  name: "",
  address: "",
  contact_email: "",
  contact_phone: "",
  is_active: true,
  storage_quota_gb: "20",
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const initials = (name) =>
  String(name ?? "")
    .split(" ")
    .map((part) => part?.[0])
    .filter(Boolean)
    .join("")
    .slice(0, 2)
    .toUpperCase() || "?";

const buildBillingPayload = (billing = {}) => {
  const payload = {};
  for (const [key] of BILLING_FIELDS) {
    payload[key] = billing?.[key]?.trim() || null;
  }
  return payload;
};

const InfoItem = ({ label, value }) => (
  <div className="min-w-0">
    <dt className="text-xs text-[var(--xl-mut)]">{label}</dt>
    <dd className="text-sm mt-1 break-words text-[var(--xl-text)]">{value || "—"}</dd>
  </div>
);

const Pager = ({ page, totalPages, total, noun, loading, onPrev, onNext, testId }) => {
  if (!total || totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-[var(--xl-line)]">
      <p className="text-xs text-[var(--xl-mut)]">
        Page {page} of {totalPages} · {total} {noun}
      </p>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={page <= 1 || loading}
          onClick={onPrev}
          className="h-8 gap-1"
          data-testid={`${testId}-prev`}
        >
          <ChevronLeft className="w-4 h-4" /> Prev
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={page >= totalPages || loading}
          onClick={onNext}
          className="h-8 gap-1"
          data-testid={`${testId}-next`}
        >
          Next <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

const CompanyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { permissions } = useAuth();
  const canManage = can(permissions, "manage_reference_data");

  const [company, setCompany] = useState(null);
  const [companyLoading, setCompanyLoading] = useState(true);
  const [companyError, setCompanyError] = useState("");

  const [users, setUsers] = useState([]);
  const [userTotal, setUserTotal] = useState(0);
  const [userPage, setUserPage] = useState(1);
  const [userSearch, setUserSearch] = useState("");
  const [userSearchInput, setUserSearchInput] = useState("");
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState("");

  const [hospitals, setHospitals] = useState([]);
  const [hospitalTotal, setHospitalTotal] = useState(0);
  const [hospitalPage, setHospitalPage] = useState(1);
  const [hospitalSearch, setHospitalSearch] = useState("");
  const [hospitalSearchInput, setHospitalSearchInput] = useState("");
  const [hospitalsLoading, setHospitalsLoading] = useState(true);

  const [saving, setSaving] = useState(false);
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [companyForm, setCompanyForm] = useState({
    name: "",
    address: "",
    contact_email: "",
    contact_phone: "",
    is_active: true,
    storage_quota_gb: "20",
    billing: { ...emptyBilling },
  });
  const [hospitalDialogOpen, setHospitalDialogOpen] = useState(false);
  const [editingHospital, setEditingHospital] = useState(null);
  const [hospitalForm, setHospitalForm] = useState(emptyHospitalForm);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [section, setSection] = useState("users");

  const fetchCompany = useCallback(async () => {
    if (!id) return;
    setCompanyLoading(true);
    setCompanyError("");
    try {
      const res = await axios.get(`${API}/organizations/${id}`);
      const org = res.data;
      if (org?.type && org.type !== "company") {
        setCompany(null);
        setCompanyError("This record is not a company.");
        return;
      }
      setCompany(org ?? null);
      if (!org) setCompanyError("Company not found");
    } catch (e) {
      console.error(e);
      setCompany(null);
      setCompanyError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load company");
    } finally {
      setCompanyLoading(false);
    }
  }, [id]);

  const fetchUsers = useCallback(async () => {
    if (!id) return;
    setUsersLoading(true);
    setUsersError("");
    try {
      const res = await axios.get(`${API}/users`, {
        params: {
          organization_id: id,
          page: userPage,
          limit: PAGE_SIZE,
          search: userSearch || undefined,
        },
      });
      setUsers(res.data?.users ?? []);
      setUserTotal(res.data?.total ?? 0);
    } catch (e) {
      console.error(e);
      setUsers([]);
      setUserTotal(0);
      setUsersError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load users");
    } finally {
      setUsersLoading(false);
    }
  }, [id, userPage, userSearch]);

  const fetchHospitals = useCallback(async () => {
    if (!id) return;
    setHospitalsLoading(true);
    try {
      const res = await axios.get(`${API}/organizations`, {
        params: {
          type: "hospital",
          comes_under: id,
          page: hospitalPage,
          limit: PAGE_SIZE,
          search: hospitalSearch || undefined,
        },
      });
      const items = res.data?.organizations ?? res.data?.items ?? [];
      setHospitals(items);
      setHospitalTotal(res.data?.total ?? items.length);
    } catch (e) {
      console.error(e);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load hospitals");
      setHospitals([]);
      setHospitalTotal(0);
    } finally {
      setHospitalsLoading(false);
    }
  }, [hospitalPage, hospitalSearch, id]);

  useEffect(() => {
    fetchCompany();
  }, [fetchCompany]);

  useEffect(() => {
    if (!company?.id) return;
    fetchUsers();
  }, [company?.id, fetchUsers]);

  useEffect(() => {
    if (!company?.id) return;
    fetchHospitals();
  }, [company?.id, fetchHospitals]);

  const openCompanyEdit = () => {
    if (!company) return;
    setCompanyForm({
      name: company.name ?? "",
      address: company.address ?? "",
      contact_email: company.contact_email ?? "",
      contact_phone: company.contact_phone ?? "",
      is_active: company.is_active !== false,
      storage_quota_gb: bytesToGbInput(company.storage_quota_bytes),
      billing: { ...emptyBilling, ...(company.billing ?? {}) },
    });
    setCompanyDialogOpen(true);
  };

  const handleSaveCompany = async () => {
    if (!company?.id) return;
    if (!companyForm.name?.trim()) {
      toast.error("Name is required");
      return;
    }
    const companyQuota = gbInputToBytes(companyForm.storage_quota_gb);
    if (Number.isNaN(companyQuota)) {
      toast.error("Storage quota must be a non-negative number of GB");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/organizations/${company.id}`, {
        name: companyForm.name.trim(),
        address: companyForm.address.trim() || null,
        contact_email: companyForm.contact_email.trim() || null,
        contact_phone: companyForm.contact_phone.trim() || null,
        is_active: companyForm.is_active !== false,
        storage_quota_bytes: companyQuota,
        billing: buildBillingPayload(companyForm.billing),
      });
      toast.success("Company updated");
      setCompanyDialogOpen(false);
      await fetchCompany();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update company");
    } finally {
      setSaving(false);
    }
  };

  const openCreateHospital = () => {
    setEditingHospital(null);
    setHospitalForm(emptyHospitalForm);
    setHospitalDialogOpen(true);
  };

  const openEditHospital = (hospital, event) => {
    event?.stopPropagation?.();
    if (!hospital?.id) return;
    setEditingHospital(hospital);
    setHospitalForm({
      name: hospital.name ?? "",
      address: hospital.address ?? "",
      contact_email: hospital.contact_email ?? "",
      contact_phone: hospital.contact_phone ?? "",
      is_active: hospital.is_active !== false,
      storage_quota_gb: bytesToGbInput(hospital.storage_quota_bytes),
    });
    setHospitalDialogOpen(true);
  };

  const handleSaveHospital = async () => {
    if (!id) return;
    if (!hospitalForm.name?.trim()) {
      toast.error("Name is required");
      return;
    }
    const hospitalQuota = gbInputToBytes(hospitalForm.storage_quota_gb);
    if (Number.isNaN(hospitalQuota)) {
      toast.error("Storage quota must be a non-negative number of GB");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: hospitalForm.name.trim(),
        type: "hospital",
        address: hospitalForm.address.trim() || null,
        contact_email: hospitalForm.contact_email.trim() || null,
        contact_phone: hospitalForm.contact_phone.trim() || null,
        comes_under: id,
        is_active: hospitalForm.is_active !== false,
        storage_quota_bytes: hospitalQuota,
      };
      if (editingHospital?.id) {
        await axios.put(`${API}/organizations/${editingHospital.id}`, payload);
        toast.success("Hospital updated");
      } else {
        await axios.post(`${API}/organizations`, payload);
        toast.success("Hospital created");
      }
      setHospitalDialogOpen(false);
      await fetchHospitals();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save hospital");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteHospital = async () => {
    if (!deleteTarget?.id) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/organizations/${deleteTarget.id}`);
      toast.success("Hospital deleted");
      setDeleteTarget(null);
      await fetchHospitals();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to delete hospital");
    } finally {
      setSaving(false);
    }
  };

  const handleHospitalClick = (hospital) => {
    if (!hospital?.id) return;
    navigate(`/patients?organization_id=${encodeURIComponent(hospital.id)}`);
  };

  const userPages = Math.max(1, Math.ceil(userTotal / PAGE_SIZE));
  const hospitalPages = Math.max(1, Math.ceil(hospitalTotal / PAGE_SIZE));

  const backButton = (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => navigate("/organizations")}
      data-testid="back-to-companies"
      className="gap-1 h-9 px-2"
      aria-label="Back to companies"
    >
      <ArrowLeft className="w-4 h-4" />
      <span className="hidden sm:inline">Companies</span>
    </Button>
  );

  return (
    <div className="flex flex-col gap-4 sm:gap-5 pb-4" data-testid="company-detail-page">
      <PageHeaderTitleOverride
        title={company?.name || "Company"}
        description="Company profile, users, and hospitals."
      />
      <PageHeaderActionsLeading>{backButton}</PageHeaderActionsLeading>

      {companyLoading ? (
        <p className="text-sm text-muted-foreground py-8 text-center">Loading company…</p>
      ) : companyError || !company ? (
        <Card className="p-6 rounded-2xl border border-[var(--xl-line)] bg-white">
          <p className="text-sm text-muted-foreground">{companyError || "Company not found"}</p>
        </Card>
      ) : (
        <>
          <Card
            className="w-full rounded-2xl border border-[var(--xl-line)] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)] p-4"
            data-testid="company-basic-card"
          >
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
              <div className="flex items-center gap-3 min-w-0 lg:w-64 lg:flex-shrink-0">
                <div className="w-9 h-9 rounded-full grid place-items-center bg-[var(--xl-primary)]/10 text-[var(--xl-primary)] flex-shrink-0" aria-hidden>
                  <Building2 className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-sm font-semibold leading-snug truncate">{company.name || "Company"}</h2>
                  <Badge
                    variant="outline"
                    className={
                      company.is_active !== false
                        ? "mt-1 bg-emerald-50 text-emerald-700 border-emerald-200"
                        : "mt-1 bg-slate-100 text-slate-600 border-slate-200"
                    }
                  >
                    {company.is_active !== false ? "Active" : "Inactive"}
                  </Badge>
                </div>
              </div>
              <dl className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4 flex-1 min-w-0">
                <InfoItem label="Email" value={company.contact_email} />
                <InfoItem label="Phone" value={company.contact_phone} />
                <InfoItem label="Address" value={company.address} />
                <InfoItem label="Cases" value={String(company.cases ?? 0)} />
                <InfoItem label="Active logins" value={String(company.active_logins ?? 0)} />
                <InfoItem
                  label="Quota (GB)"
                  value={formatQuotaRatio(company.storage_used_bytes, company.storage_quota_bytes)}
                />
              </dl>
              {canManage ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 flex-shrink-0 self-end lg:self-center"
                  onClick={openCompanyEdit}
                  aria-label="Edit company"
                  data-testid="edit-company-btn"
                >
                  <Pencil className="w-4 h-4" />
                </Button>
              ) : null}
            </div>
          </Card>

          <Card className="overflow-hidden rounded-2xl border border-[var(--xl-line)] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between p-4 sm:px-5 border-b border-[var(--xl-line)]">
              <Tabs value={section} onValueChange={setSection} className="w-full sm:w-auto">
                <TabsList className="h-10 w-full sm:w-auto">
                  <TabsTrigger value="users" data-testid="company-tab-users" className="flex-1 sm:flex-none px-4">
                    Users ({userTotal})
                  </TabsTrigger>
                  <TabsTrigger value="hospitals" data-testid="company-tab-hospitals" className="flex-1 sm:flex-none px-4">
                    Hospitals ({hospitalTotal})
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              {section === "users" ? (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    setUserPage(1);
                    setUserSearch(userSearchInput.trim());
                  }}
                  className="flex items-center gap-2 w-full lg:w-auto"
                >
                  <div className="relative min-w-0 flex-1 sm:w-56">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--xl-mut2)] pointer-events-none" />
                    <Input
                      value={userSearchInput}
                      onChange={(e) => setUserSearchInput(e.target.value)}
                      placeholder="Search users..."
                      aria-label="Search users"
                      data-testid="company-user-search"
                      className="h-9 pl-9 text-sm"
                    />
                  </div>
                  <Button type="submit" variant="outline" className="h-9">Search</Button>
                </form>
              ) : (
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full lg:w-auto">
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      setHospitalPage(1);
                      setHospitalSearch(hospitalSearchInput.trim());
                    }}
                    className="flex items-center gap-2"
                  >
                    <div className="relative min-w-0 flex-1 sm:w-56">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--xl-mut2)] pointer-events-none" />
                      <Input
                        value={hospitalSearchInput}
                        onChange={(e) => setHospitalSearchInput(e.target.value)}
                        placeholder="Search hospitals..."
                        aria-label="Search hospitals"
                        data-testid="company-hospital-search"
                        className="h-9 pl-9 text-sm"
                      />
                    </div>
                    <Button type="submit" variant="outline" className="h-9">Search</Button>
                  </form>
                  {canManage ? (
                    <Button
                      type="button"
                      className="h-9 gap-1.5"
                      onClick={openCreateHospital}
                      data-testid="add-hospital-btn"
                    >
                      <Plus className="w-4 h-4" />
                      Add hospital
                    </Button>
                  ) : null}
                </div>
              )}
            </div>
            {section === "users" ? (
              <>
                <div className="overflow-auto">
                  <Table className="sticky-table min-w-[720px]">
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Organization</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {usersLoading ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Loading…</TableCell>
                        </TableRow>
                      ) : usersError ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">{usersError}</TableCell>
                        </TableRow>
                      ) : users.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No users found</TableCell>
                        </TableRow>
                      ) : (
                        users.map((user, index) => (
                          <TableRow key={user?.id ?? index} data-testid={`company-user-row-${index}`}>
                            <TableCell>
                              <div className="flex items-center gap-2.5 min-w-0">
                                <Avatar className="w-8 h-8">
                                  {user?.avatar_url ? <AvatarImage src={user.avatar_url} /> : null}
                                  <AvatarFallback className="text-[10px] bg-secondary">
                                    {initials(user?.name)}
                                  </AvatarFallback>
                                </Avatar>
                                <span className="font-medium text-sm truncate">{user?.name || "—"}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">{user?.email || "—"}</TableCell>
                            <TableCell>
                              <Badge className={`text-[10px] capitalize ${ROLE_COLORS[user?.role] ?? ROLE_COLORS.viewer}`}>
                                {ROLE_LABELS[user?.role] ?? user?.role?.replace(/_/g, " ") ?? "—"}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm">{user?.organization || "—"}</TableCell>
                            <TableCell>
                              <Badge variant={user?.status === "active" ? "default" : "secondary"} className="text-[10px]">
                                {user?.status || "—"}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
                <Pager
                  page={userPage}
                  totalPages={userPages}
                  total={userTotal}
                  noun="users"
                  loading={usersLoading}
                  onPrev={() => setUserPage((page) => Math.max(1, page - 1))}
                  onNext={() => setUserPage((page) => Math.min(userPages, page + 1))}
                  testId="company-users"
                />
              </>
            ) : (
              <>
                <div className="overflow-auto">
                  <Table className="sticky-table min-w-[760px]">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="min-w-[200px]">Hospital</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Address</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Created</TableHead>
                        {canManage ? <TableHead className="text-right">Actions</TableHead> : null}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {hospitalsLoading ? (
                        <TableRow>
                          <TableCell colSpan={canManage ? 6 : 5} className="text-center py-8 text-muted-foreground">
                            Loading…
                          </TableCell>
                        </TableRow>
                      ) : hospitals.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={canManage ? 6 : 5} className="text-center py-8 text-muted-foreground">
                            No hospitals found
                          </TableCell>
                        </TableRow>
                      ) : (
                        hospitals.map((hospital, index) => (
                          <TableRow
                            key={hospital?.id ?? index}
                            data-testid={`company-hospital-row-${index}`}
                            className="cursor-pointer hover:bg-black/[0.02]"
                            onClick={() => handleHospitalClick(hospital)}
                          >
                            <TableCell className="font-medium">{hospital?.name || "—"}</TableCell>
                            <TableCell>
                              <div className="min-w-0">
                                <div className="truncate">{hospital?.contact_email || "—"}</div>
                                {hospital?.contact_phone ? (
                                  <div className="text-xs text-[var(--xl-mut)] mt-0.5 truncate">{hospital.contact_phone}</div>
                                ) : null}
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="line-clamp-2">{hospital?.address || "—"}</span>
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className={
                                  hospital?.is_active !== false
                                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                    : "bg-slate-100 text-slate-600 border-slate-200"
                                }
                              >
                                {hospital?.is_active !== false ? "Active" : "Inactive"}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-[var(--xl-mut)]">{formatDate(hospital?.created_at)}</TableCell>
                            {canManage ? (
                              <TableCell>
                                <div className="flex items-center justify-end gap-1">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    aria-label={`Edit ${hospital?.name || "hospital"}`}
                                    data-testid={`edit-hospital-${index}`}
                                    onClick={(event) => openEditHospital(hospital, event)}
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-destructive hover:text-destructive"
                                    aria-label={`Delete ${hospital?.name || "hospital"}`}
                                    data-testid={`delete-hospital-${index}`}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      setDeleteTarget(hospital);
                                    }}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            ) : null}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
                <Pager
                  page={hospitalPage}
                  totalPages={hospitalPages}
                  total={hospitalTotal}
                  noun="hospitals"
                  loading={hospitalsLoading}
                  onPrev={() => setHospitalPage((page) => Math.max(1, page - 1))}
                  onNext={() => setHospitalPage((page) => Math.min(hospitalPages, page + 1))}
                  testId="company-hospitals"
                />
              </>
            )}
          </Card>
        </>
      )}

      <Dialog open={companyDialogOpen} onOpenChange={setCompanyDialogOpen}>
        <DialogContent className="w-[min(100%,40rem)] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit company</DialogTitle>
            <DialogDescription>Update company contact and billing details.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-1">
            <div className="grid gap-1.5">
              <Label htmlFor="company-name">Name *</Label>
              <Input
                id="company-name"
                value={companyForm.name}
                onChange={(e) => setCompanyForm((prev) => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label htmlFor="company-email">Contact email</Label>
                <Input
                  id="company-email"
                  type="email"
                  value={companyForm.contact_email}
                  onChange={(e) => setCompanyForm((prev) => ({ ...prev, contact_email: e.target.value }))}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="company-phone">Contact phone</Label>
                <Input
                  id="company-phone"
                  value={companyForm.contact_phone}
                  onChange={(e) => setCompanyForm((prev) => ({ ...prev, contact_phone: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="company-address">Address</Label>
              <Input
                id="company-address"
                value={companyForm.address}
                onChange={(e) => setCompanyForm((prev) => ({ ...prev, address: e.target.value }))}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="company-storage-quota">Storage quota (GB)</Label>
              <Input
                id="company-storage-quota"
                type="number"
                min="0"
                step="0.01"
                data-testid="company-input-storage-quota"
                value={companyForm.storage_quota_gb}
                onChange={(e) => setCompanyForm((prev) => ({ ...prev, storage_quota_gb: e.target.value }))}
                placeholder="20"
                aria-label="Storage quota in gigabytes"
              />
            </div>
            <div className="flex items-center justify-between gap-3 rounded-lg border border-[var(--xl-line)] px-3 py-2.5">
              <div className="min-w-0">
                <Label htmlFor="company-active">Active</Label>
                <p className="text-xs text-[var(--xl-mut)] mt-0.5">
                  Inactive companies stay in the list but are marked inactive
                </p>
              </div>
              <Switch
                id="company-active"
                checked={companyForm.is_active !== false}
                onCheckedChange={(checked) => setCompanyForm((prev) => ({ ...prev, is_active: Boolean(checked) }))}
                data-testid="company-input-active"
                aria-label="Company active"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {BILLING_FIELDS.map(([key, label, placeholder]) => (
                <div key={key} className={`grid gap-1.5 ${key === "billing_address" ? "sm:col-span-2" : ""}`}>
                  <Label htmlFor={`company-${key}`}>{label}</Label>
                  <Input
                    id={`company-${key}`}
                    type={key.includes("email") ? "email" : "text"}
                    value={companyForm.billing?.[key] ?? ""}
                    placeholder={placeholder}
                    onChange={(e) => setCompanyForm((prev) => ({
                      ...prev,
                      billing: { ...prev.billing, [key]: e.target.value },
                    }))}
                  />
                </div>
              ))}
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setCompanyDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSaveCompany} disabled={saving} data-testid="save-company-btn">
              {saving ? "Saving..." : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={hospitalDialogOpen} onOpenChange={setHospitalDialogOpen}>
        <DialogContent className="w-[min(100%,28rem)]">
          <DialogHeader>
            <DialogTitle>{editingHospital ? "Edit hospital" : "Create hospital"}</DialogTitle>
            <DialogDescription>
              {editingHospital
                ? "Update this hospital."
                : `Add a hospital under ${company?.name || "this company"}.`}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-1">
            <div className="grid gap-1.5">
              <Label htmlFor="hospital-name">Name *</Label>
              <Input
                id="hospital-name"
                data-testid="hospital-input-name"
                value={hospitalForm.name}
                onChange={(e) => setHospitalForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Hospital name"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="hospital-email">Contact email</Label>
              <Input
                id="hospital-email"
                type="email"
                data-testid="hospital-input-email"
                value={hospitalForm.contact_email}
                onChange={(e) => setHospitalForm((prev) => ({ ...prev, contact_email: e.target.value }))}
                placeholder="contact@example.com"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="hospital-phone">Contact phone</Label>
              <Input
                id="hospital-phone"
                data-testid="hospital-input-phone"
                value={hospitalForm.contact_phone}
                onChange={(e) => setHospitalForm((prev) => ({ ...prev, contact_phone: e.target.value }))}
                placeholder="+1-555-0123"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="hospital-address">Address</Label>
              <Input
                id="hospital-address"
                data-testid="hospital-input-address"
                value={hospitalForm.address}
                onChange={(e) => setHospitalForm((prev) => ({ ...prev, address: e.target.value }))}
                placeholder="Street, city"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="hospital-storage-quota">Storage quota (GB)</Label>
              <Input
                id="hospital-storage-quota"
                type="number"
                min="0"
                step="0.01"
                data-testid="hospital-input-storage-quota"
                value={hospitalForm.storage_quota_gb}
                onChange={(e) => setHospitalForm((prev) => ({ ...prev, storage_quota_gb: e.target.value }))}
                placeholder="20"
                aria-label="Storage quota in gigabytes"
              />
            </div>
            <div className="flex items-center justify-between gap-3 rounded-lg border border-[var(--xl-line)] px-3 py-2.5">
              <div className="min-w-0">
                <Label htmlFor="hospital-active">Active</Label>
                <p className="text-xs text-[var(--xl-mut)] mt-0.5">
                  Inactive hospitals stay in the list but are marked inactive
                </p>
              </div>
              <Switch
                id="hospital-active"
                checked={hospitalForm.is_active !== false}
                onCheckedChange={(checked) => setHospitalForm((prev) => ({ ...prev, is_active: Boolean(checked) }))}
                data-testid="hospital-input-active"
                aria-label="Hospital active"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setHospitalDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSaveHospital} disabled={saving} data-testid="hospital-save-btn">
              {saving ? "Saving..." : editingHospital ? "Save changes" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.name || "hospital"}?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the hospital. Users assigned to it must be reassigned first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={(e) => {
                e.preventDefault();
                handleDeleteHospital();
              }}
              disabled={saving}
              data-testid="hospital-confirm-delete"
            >
              {saving ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CompanyDetail;
